import BiwengerIcon from '@/components/shared/icons/biwenger-icon';
import FantasyLaLigaIcon from '@/components/shared/icons/fantasy-la-liga-icon';
import CustomModal from '@/DesignSystem/components/custom-modal/CustomModal';
import { Box, Typography, Divider } from '@mui/material';
import FantasyMarcaIcon from '@/components/shared/icons/FantasyMarcaIcon';
import MisterFantasyIcon from '@/components/shared/icons/MisterFantasyIcon';
import useShowPlayerAlineacionProbable from '@/components/alineaciones/shared/hooks/useShowPlayerAlineacionProbable';
import { FANTASY, FANTASY_MODE } from '@/helpers/biwenger-helper';
import Logo from '../logo';
import PuntuacionConfirmada from '@/components/alineaciones/shared/complementos/PuntuacionConfirmada';
import { useGlobalRankings } from '@/hooks/useGlobalRankings';
import { usePlayerRankings } from '@/hooks/usePlayerRankings';
import FantasyIconSelector from '@/components/shared/icons/FantasyIconSelector';

const JugadorLineupAlineacionConfirmadaModal = ({ player, open }) => {
    const handleClose = useShowPlayerAlineacionProbable(null);
    const playerRanking = usePlayerRankings(player?.id);
    const { rankings } = useGlobalRankings();
    const allPlayerRankings = player?.id ? rankings[player.id] : null;

    // Función para obtener el nombre del modo
    const getModeName = (mode) => {
        switch (mode) {
            case FANTASY.ANALITICA:
                return 'Analítica Fantasy';
            case FANTASY.FANTASY:
                return 'La Liga Fantasy';
            case FANTASY.BIWENGER:
                return 'Biwenger';
            case FANTASY.FANTASY_MARCA:
                return 'Fantasy Marca';
            case FANTASY.MISTER_FANTASY:
                return 'Mister Fantasy';
            default:
                return `Modo ${mode}`;
        }
    };

    // Función para obtener el nombre del submode de Biwenger
    const getBiwengerSubModeName = (subMode) => {
        switch (subMode) {
            case FANTASY_MODE.TITULARIDAD:
                return 'Titularidad';
            case FANTASY_MODE.DIARIO_AS:
                return 'Diario AS';
            case FANTASY_MODE.SOFASCORE:
                return 'Sofascore';
            case FANTASY_MODE.STATS:
                return 'Stats';
            case FANTASY_MODE.AVERAGE:
                return 'Promedio';
            case FANTASY_MODE.SOCIAL:
                return 'Social';
            case FANTASY_MODE.CARRUSEL_DEPORTIVO:
                return 'Carrusel Deportivo';
            default:
                return `Submodo ${subMode}`;
        }
    };

    // Función para obtener el icono del modo
    const getModeIcon = (mode) => {
        switch (mode) {
            case FANTASY.ANALITICA:
                return <Logo width={16} height={16} disableClick={true} />;
            case FANTASY.FANTASY:
                return <FantasyLaLigaIcon iconWidth={16} iconHeight={16} />;
            case FANTASY.BIWENGER:
                return <BiwengerIcon width={16} height={16} />;
            case FANTASY.FANTASY_MARCA:
                return <FantasyMarcaIcon iconWidth={16} iconHeight={16} />;
            case FANTASY.MISTER_FANTASY:
                return <MisterFantasyIcon iconWidth={16} iconHeight={16} />;
            default:
                return null;
        }
    };

    // Agrupar estadísticas por modo
    const statsByMode = {};
    player.estadisticas?.forEach((stat) => {
        if (!statsByMode[stat.mode]) {
            statsByMode[stat.mode] = [];
        }
        statsByMode[stat.mode].push(stat);
    });

    return (
        <CustomModal
            open={open}
            onClose={handleClose}
            title={player.name}
            contentSx={{
                maxHeight: '70vh',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                    width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '4px',
                    '&:hover': {
                        background: '#a8a8a8',
                    },
                },
            }}
        >
            {/* Estadísticas del partido */}
            {Object.entries(statsByMode).map(([mode, stats], index) => (
                <Box key={mode} sx={{ mb: index < Object.keys(statsByMode).length - 1 ? 1 : 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        {getModeIcon(parseInt(mode))}
                        <Typography variant="h6" sx={{ fontSize: '14px' }}>
                            {getModeName(parseInt(mode))}
                        </Typography>
                    </Box>

                    {mode == FANTASY.BIWENGER ? (
                        // Para Biwenger, mostrar cada submode por separado
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {stats.map((stat, statIndex) => (
                                <Box
                                    key={statIndex}
                                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                                >
                                    <Typography sx={{ fontSize: '14px' }}>
                                        {getBiwengerSubModeName(stat.subMode)}:
                                    </Typography>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
                                        <PuntuacionConfirmada
                                            estadisticaPartido={stat.estadistica}
                                            fontSize={12}
                                            width="40px"
                                            height="18px"
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            confirmedMode={parseInt(stat.mode)}
                                        />
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        // Para otros modos, mostrar solo la puntuación
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '14px' }}>Puntuación:</Typography>
                            <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
                                <PuntuacionConfirmada
                                    estadisticaPartido={stats[0]?.estadistica}
                                    fontSize={12}
                                    width="40px"
                                    height="18px"
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    confirmedMode={parseInt(stats[0]?.mode)}
                                />
                            </Typography>
                        </Box>
                    )}

                    {index < Object.keys(statsByMode).length - 1 && (
                        <Divider sx={{ mb: 1, backgroundColor: 'border.light', mt: 1 }} />
                    )}
                </Box>
            ))}

            {/* Ranking Global */}
            {playerRanking && allPlayerRankings && (
                <>
                    <Divider sx={{ my: 2, backgroundColor: 'border.light' }} />
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="h6" sx={{ fontSize: '14px', mb: 1 }}>
                            Ranking Global
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Box
                                sx={{
                                    width: 24,
                                    height: 37,
                                    borderRadius: '4px',
                                    backgroundImage: `url(${process.env.NEXT_PUBLIC_IMAGES}/cards/${playerRanking.tier}.png?width=36&height=56)`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    flexShrink: 0,
                                }}
                            />
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                                    {playerRanking.tier === 'red'
                                        ? 'Top 10 en Bajadas de Mercado'
                                        : `Top ${
                                              playerRanking.tier === 'gold'
                                                  ? '1'
                                                  : playerRanking.tier === 'silver'
                                                    ? '5'
                                                    : '10'
                                          } en ${
                                              playerRanking.category === 'mercado'
                                                  ? playerRanking.marketType === 'up'
                                                      ? 'Subidas de Mercado'
                                                      : playerRanking.marketType === 'value'
                                                        ? 'Valor de Mercado'
                                                        : 'Mercado'
                                                  : playerRanking.category === 'fantasy'
                                                    ? 'La Liga Fantasy'
                                                    : playerRanking.category === 'mister'
                                                      ? 'Mister Fantasy'
                                                      : playerRanking.category === 'marca'
                                                        ? 'Fantasy Marca'
                                                        : 'Biwenger'
                                          }`}
                                </Typography>
                                <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                                    Este jugador está en el top global en alguna de las categorías, por eso
                                    aparece con una tarjeta especial en la alineación.
                                </Typography>
                            </Box>
                        </Box>

                        {/* Mostrar todos los rankings del jugador */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                            {allPlayerRankings.laLigaFantasy && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FantasyIconSelector fantasy={FANTASY.FANTASY} size={16} />
                                    <Typography sx={{ fontSize: '13px' }}>
                                        La Liga Fantasy:{' '}
                                        <strong>TOP {allPlayerRankings.laLigaFantasy}</strong>
                                    </Typography>
                                </Box>
                            )}
                            {allPlayerRankings.fantasyMarca && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FantasyIconSelector fantasy={FANTASY.FANTASY_MARCA} size={16} />
                                    <Typography sx={{ fontSize: '13px' }}>
                                        Fantasy Marca: <strong>TOP {allPlayerRankings.fantasyMarca}</strong>
                                    </Typography>
                                </Box>
                            )}
                            {allPlayerRankings.misterFantasy && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FantasyIconSelector fantasy={FANTASY.MISTER_FANTASY} size={16} />
                                    <Typography sx={{ fontSize: '13px' }}>
                                        Mister Fantasy: <strong>TOP {allPlayerRankings.misterFantasy}</strong>
                                    </Typography>
                                </Box>
                            )}
                            {allPlayerRankings.biwenger && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FantasyIconSelector fantasy={FANTASY.BIWENGER} size={16} />
                                    <Typography sx={{ fontSize: '13px' }}>
                                        Biwenger: <strong>TOP {allPlayerRankings.biwenger}</strong>
                                    </Typography>
                                </Box>
                            )}
                            {allPlayerRankings.marketValueUp && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: '13px' }}>📈</Typography>
                                    <Typography sx={{ fontSize: '13px' }}>
                                        Subidas de Mercado:{' '}
                                        <strong>TOP {allPlayerRankings.marketValueUp}</strong>
                                    </Typography>
                                </Box>
                            )}
                            {allPlayerRankings.marketValue && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: '13px' }}>💰</Typography>
                                    <Typography sx={{ fontSize: '13px' }}>
                                        Valor de Mercado: <strong>TOP {allPlayerRankings.marketValue}</strong>
                                    </Typography>
                                </Box>
                            )}
                            {allPlayerRankings.marketValueDown && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: '13px' }}>📉</Typography>
                                    <Typography sx={{ fontSize: '13px' }}>
                                        Bajadas de Mercado:{' '}
                                        <strong>TOP {allPlayerRankings.marketValueDown}</strong>
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </>
            )}
        </CustomModal>
    );
};

export default JugadorLineupAlineacionConfirmadaModal;
